import { BRAND } from "../config/brand";
import {
  STAT_KEYS,
  type Align,
  type Brand,
  type Density,
  type Format,
  type Mode,
  type RadiusKind,
  type ShadowKind,
  type Size,
  type StatKey,
  type StatsConfig,
  type Theme,
} from "./types";

const ENUMS = {
  mode: new Set<Mode>(["cards", "inline", "hero", "stacked"]),
  theme: new Set<Theme>(["light", "dark", "transparent", "auto"]),
  density: new Set<Density>(["compact", "cozy", "comfortable"]),
  size: new Set<Size>(["sm", "md", "lg"]),
  align: new Set<Align>(["left", "center", "right"]),
  format: new Set<Format>(["long", "compact"]),
  brand: new Set<Brand>(["minimal", "full", "none"]),
  shadow: new Set<ShadowKind>(["soft", "none", "elevated"]),
  radius: new Set<RadiusKind>(["lg", "md", "none"]),
} as const;

function pick<T extends string>(value: string | null, allowed: Set<T>, fallback: T): T {
  return value && (allowed as Set<string>).has(value) ? (value as T) : fallback;
}

function bool(value: string | null, fallback: boolean): boolean {
  if (value == null) return fallback;
  if (value === "1" || value === "true" || value === "yes") return true;
  if (value === "0" || value === "false" || value === "no") return false;
  return fallback;
}

function hex(value: string | null, fallback?: string): string | undefined {
  if (!value) return fallback;
  return `#${value.replace(/^#/, "")}`;
}

function int(value: string | null, fallback: number, min = 0, max = Number.MAX_SAFE_INTEGER): number {
  if (value == null) return fallback;
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function parseShow(value: string | null): StatKey[] {
  if (!value) return [...STAT_KEYS];
  const wanted = value
    .split(/[,\s]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  // Map common aliases
  const aliasMap: Record<string, StatKey> = {
    city: "cities",
    cities: "cities",
    location: "locations",
    locations: "locations",
    ac: "ac",
    "ac-sockets": "ac",
    "ac_sockets": "ac",
    dc: "dc",
    "dc-sockets": "dc",
    "dc_sockets": "dc",
  };
  const out: StatKey[] = [];
  const seen = new Set<StatKey>();
  for (const w of wanted) {
    const k = aliasMap[w];
    if (k && !seen.has(k)) {
      out.push(k);
      seen.add(k);
    }
  }
  return out.length ? out : [...STAT_KEYS];
}

function parseLabels(sp: URLSearchParams): Partial<Record<StatKey, string>> {
  const out: Partial<Record<StatKey, string>> = {};
  // Two syntaxes supported:
  //   ?label.cities=Şehirler
  //   ?label_cities=Şehirler
  const aliasToKey: Record<string, StatKey> = {
    cities: "cities",
    locations: "locations",
    ac: "ac",
    dc: "dc",
  };
  sp.forEach((value, key) => {
    const m = key.match(/^label[._-](.+)$/i);
    if (!m) return;
    const k = aliasToKey[m[1].toLowerCase()];
    if (k) out[k] = value;
  });
  return out;
}

export function readStatsConfig(): StatsConfig {
  const sp =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

  const show = parseShow(sp.get("show"));
  const heroParam = sp.get("hero");
  const heroKey: StatKey =
    heroParam && (STAT_KEYS as readonly string[]).includes(heroParam)
      ? (heroParam as StatKey)
      : show[0] ?? "locations";

  const langParam = sp.get("lang");
  const lang =
    langParam === "tr" || langParam === "en" ? (langParam as "tr" | "en") : "auto";

  return {
    mode: pick(sp.get("mode"), ENUMS.mode, "cards"),
    density: pick(sp.get("density"), ENUMS.density, "cozy"),
    size: pick(sp.get("size"), ENUMS.size, "md"),
    align: pick(sp.get("align"), ENUMS.align, "center"),

    show,
    heroKey,

    labels: parseLabels(sp),
    lang,
    noLangToggle: bool(sp.get("noLang"), false),

    theme: pick(sp.get("theme"), ENUMS.theme, "light"),
    accent: hex(sp.get("accent"), BRAND.primary)!,
    bgOverride: hex(sp.get("bg")),
    fgOverride: hex(sp.get("fg")),
    border: bool(sp.get("border"), true),
    radius: pick(sp.get("radius"), ENUMS.radius, "lg"),
    shadow: pick(sp.get("shadow"), ENUMS.shadow, "soft"),

    format: pick(sp.get("format"), ENUMS.format, "long"),
    locale: sp.get("locale") || "tr-TR",

    refreshSec: int(sp.get("refresh"), 300, 0, 86400),
    animate: bool(sp.get("animate"), true),
    autosize: bool(sp.get("autosize"), false),

    brand: pick(sp.get("brand"), ENUMS.brand, "minimal"),
  };
}
